/**
 * UpgradePrompt — inline upgrade nudge used by FeatureGate and limit banners.
 *
 * Props:
 *   title        — headline text
 *   description  — supporting text
 *   requiredPlan — plan key to link to: 'starter' | 'professional' | 'enterprise'
 *   compact      — if true, renders a smaller single-line variant
 */
import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { PLAN_DISPLAY } from '../../config/planFeatures';

const UpgradePrompt = ({
  title,
  description,
  requiredPlan = 'professional',
  compact = false,
}) => {
  const plan = PLAN_DISPLAY[requiredPlan] || PLAN_DISPLAY.professional;
  const href = requiredPlan === 'enterprise'
    ? '/authentication?intent=contact'
    : `/signup?plan=${requiredPlan}`;
  const planBadgeText = `${plan.label} — ${plan.price === 0 ? 'Free' : `$${plan.price}/mo`}`;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-warning/10 border border-warning/30 rounded-lg text-xs">
        <Icon name="Lock" size={12} color="var(--color-warning)" />
        <span className="text-foreground font-medium">{title}</span>
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${plan.badgeClasses}`}
        >
          {planBadgeText}
        </span>
        <Link
          to={href}
          className="text-primary font-semibold hover:underline ml-1"
        >
          Upgrade →
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 p-6 bg-muted/30 border-2 border-dashed border-border rounded-xl text-center">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
          <Icon name="Lock" size={22} color="var(--color-primary)" />
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap ${plan.badgeClasses}`}
        >
          {planBadgeText}
        </span>
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground mb-1">{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground max-w-xs">{description}</p>
        )}
      </div>
      <Button asChild variant="default" size="sm">
        <Link to={href}>
          {requiredPlan === 'enterprise' ? 'Contact Sales' : `Upgrade to ${plan.label}`}
        </Link>
      </Button>
    </div>
  );
};

export default UpgradePrompt;
